import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useAccount } from '../context/AccountContext';
import { useProperty } from '../context/PropertyContext';
import '../styles/RevenueOptimizationDashboard.css';
import DesktopLayout from '../layouts/DesktopLayout';
import DateRangeSelector from '../components/revenue/DateRangeSelector';
import PropertySwitcher from '../components/common/PropertySwitcher';
import PerformanceMetrics from '../components/revenue/PerformanceMetrics';
import PlatformBreakdown from '../components/revenue/PlatformBreakdown';
import AccountSwitcher from '../components/common/AccountSwitcher';
import MonthlyReportsSection from '../components/revenue/MonthlyReports/MonthlyReportsSection';
import { formatWeekdayName, formatDate, formatRelativeTime } from '../utils/dateFormatter';
import { todayLocalIso } from '../utils/dateValueHelper';
import useLanguage from '../hooks/useLanguage';

/**
 * Revenue Optimization Dashboard
 *
 * Transforms basic finance management into actionable revenue insights
 * Features:
 * - Performance metrics (Occupancy, Average Daily Rate, Total Revenue)
 * - Platform breakdown with commission transparency
 * - Period comparison (current vs previous)
 * - Multi-property support
 */
const RevenueOptimizationDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { t } = useLanguage();
  const { selectedAccount } = useAccount(); // Global account selection from Context
  const { selectedProperty } = useProperty(); // Global property selection from Context
  const [activeNavItem, setActiveNavItem] = useState('finance');

  // TurnKey owners get a simplified finance view — internal insight/alert tooling is hidden for them
  const isTurnKeyOwner = user?.role === 'turnKeyOwner';

  // Data state
  const [metrics, setMetrics] = useState(null);
  // Window still open → the figures cover elapsed nights only (backend-derived).
  const [periodInProgress, setPeriodInProgress] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [properties, setProperties] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // An account with no units yet is a fact, not a failure — shown as information (step 7).
  const [noUnits, setNoUnits] = useState(false);
  const [period, setPeriod] = useState('thisYear');
  // Custom-range fallbacks default to year-to-date (Jan 1 → today), computed dynamically
  const [customStartDate, setCustomStartDate] = useState(`${new Date().getFullYear()}-01-01`);
  const [customEndDate, setCustomEndDate] = useState(todayLocalIso());
  const [lastUpdated, setLastUpdated] = useState(null);

  // Occupancy alerts state
  const [occupancyAlerts, setOccupancyAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [generatingAlerts, setGeneratingAlerts] = useState(false);

  // Collapsible sections state
  const [insightsExpanded, setInsightsExpanded] = useState(false);
  const [platformExpanded, setPlatformExpanded] = useState(false);
  const [dayOfWeekExpanded, setDayOfWeekExpanded] = useState(false);
  const [alertsExpanded, setAlertsExpanded] = useState(false);

  // Helper function to format date range for display
  const getDateRangeText = () => {
    if (period === 'custom' && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      // `year: undefined` drops the year the shared default adds — day + month only.
      const startStr = formatDate(start, { year: undefined });
      const endStr = formatDate(end, { year: undefined });
      return `(من ${startStr} إلى ${endStr})`;
    }

    // For preset periods, calculate the range
    const periodMap = {
      'last7days': 'آخر 7 أيام',
      'last30days': 'آخر 30 يوم',
      'last90days': 'آخر 90 يوم',
      'thisMonth': 'هذا الشهر',
      'lastMonth': 'الشهر الماضي',
      'thisYear': 'هذه السنة'
    };

    return periodMap[period] ? `(${periodMap[period]})` : '';
  };

  // Fetch user properties (wrapped in useCallback to avoid dependency warnings)
  const fetchProperties = useCallback(async () => {
    try {
      // A retry that succeeds must not leave the previous failure on screen
      setError(null);
      const params = {};
      // Add ownerId if selected (for admin/ops filtering)
      if (selectedAccount) {
        params.ownerId = selectedAccount;
      }

      const response = await axios.get('/api/v1/properties', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params
      });

      // The API returns array directly (as per securePropertyRoutes.js)
      let userProperties = [];

      if (Array.isArray(response.data)) {
        userProperties = response.data;
      } else if (response.data && response.data.properties) {
        userProperties = response.data.properties;
      } else if (response.data && response.data.data) {
        userProperties = response.data.data;
      } else {
        console.warn('📊 Unexpected properties response format:', response.data);
      }

      setProperties(userProperties);
      setNoUnits(userProperties.length === 0);

      // PropertySwitcher handles auto-select for single property
      if (userProperties.length === 0) {
        // No properties, stop loading
        setLoading(false);
      }
    } catch (err) {
      console.error('❌ Error fetching properties:', err);
      setError('فشل في جلب العقارات');
      setLoading(false);
    }
  }, [selectedAccount]); // Refetch when account changes

  // Fetch metrics (wrapped in useCallback to avoid dependency warnings)
  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Don't fetch if no property is selected
      if (!selectedProperty && properties.length === 0) {
        setLoading(false);
        return;
      }

      // Explicit validation: Custom period requires dates
      if (period === 'custom' && (!customStartDate || !customEndDate)) {
        setLoading(false);
        return; // Will retry when dates populate (useEffect dependency)
      }

      // Determine API endpoint
      const isPortfolio = !selectedProperty;
      const endpoint = isPortfolio
        ? '/api/v1/revenue/portfolio/metrics'
        : `/api/v1/revenue/${selectedProperty}/metrics`;

      // Build query parameters
      const params = { period };
      if (period === 'custom' && customStartDate && customEndDate) {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      // Add ownerId if selected (for admin/ops filtering)
      if (selectedAccount) {
        params.ownerId = selectedAccount;
      }

      // Fetch metrics
      const metricsResponse = await axios.get(endpoint, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        params
      });

      if (metricsResponse.data.success) {
        setMetrics(metricsResponse.data.metrics);
        // The backend owns "has this window closed yet" — it knows each preset's end.
        setPeriodInProgress(!!metricsResponse.data.dateRange?.inProgress);
      } else {
        console.warn('📈 Metrics response not successful:', metricsResponse.data);
        setMetrics(null);
        setPeriodInProgress(false);
      }

      // Fetch comparison if single property
      if (!isPortfolio) {
        try {
          const comparisonResponse = await axios.get(
            `/api/v1/revenue/${selectedProperty}/comparison`,
            {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
              params
            }
          );

          if (comparisonResponse.data.success) {
            setComparison(comparisonResponse.data.comparison);
          }
        } catch (err) {
          setComparison(null);
        }
      } else {
        setComparison(null);
      }

      // Update last updated timestamp
      setLastUpdated(new Date());

    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError('فشل في جلب البيانات');
      // Never leave the previous period's money under the new period's selector (step 7).
      setMetrics(null);
      setComparison(null);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, period, customStartDate, customEndDate, properties.length, selectedAccount]); // Dependencies

  // Fetch occupancy alerts for selected property
  const fetchOccupancyAlerts = useCallback(async () => {
    try {
      if (!selectedProperty) {
        setOccupancyAlerts([]);
        return;
      }

      setAlertsLoading(true);
      const response = await axios.get(`/api/v1/alerts/occupancy/property/${selectedProperty}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success) {
        // Filter for critical alerts only: high severity OR new status
        const criticalAlerts = response.data.data
          .filter(alert => {
            // Exclude resolved and dismissed
            if (alert.status === 'resolved' || alert.status === 'dismissed') return false;
            // Include only high severity OR new status
            return alert.severity === 'high' || alert.status === 'new';
          })
          .sort((a, b) => {
            // Priority 1: Status (new first)
            const statusOrder = { 'new': 0, 'viewed': 1, 'acknowledged': 2 };
            const statusDiff = (statusOrder[a.status] || 3) - (statusOrder[b.status] || 3);
            if (statusDiff !== 0) return statusDiff;

            // Priority 2: Severity (high first)
            const severityOrder = { 'high': 0, 'medium': 1, 'low': 2 };
            const severityDiff = (severityOrder[a.severity] || 3) - (severityOrder[b.severity] || 3);
            if (severityDiff !== 0) return severityDiff;

            // Priority 3: Period (shorter first - 7 days before 30 days)
            return (a.periodDays || 999) - (b.periodDays || 999);
          })
          .slice(0, 3); // Show max 3 critical alerts

        setOccupancyAlerts(criticalAlerts);
      }
    } catch (err) {
      console.error('Error fetching occupancy alerts:', err);
      setOccupancyAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  }, [selectedProperty]);

  // Generate alerts manually (testing only)
  const handleGenerateAlerts = async () => {
    if (!selectedProperty) {
      alert('يرجى اختيار عقار محدد لتوليد التنبيهات');
      return;
    }

    try {
      setGeneratingAlerts(true);
      const response = await axios.post(
        `/api/v1/alerts/occupancy/generate/${selectedProperty}`,
        {},
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        alert(`✅ تم توليد ${response.data.alerts} تنبيه بنجاح`);
        // Refresh alerts
        await fetchOccupancyAlerts();
      }
    } catch (err) {
      console.error('Error generating alerts:', err);
      alert('❌ فشل في توليد التنبيهات: ' + (err.response?.data?.message || err.message));
    } finally {
      setGeneratingAlerts(false);
    }
  };

  // Fetch properties on mount
  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  // Note: Property validation now handled by PropertySwitcher component (centralized)

  // Fetch metrics on: page load, property switch, date range change
  useEffect(() => {
    if (!properties.length && !selectedProperty) {
      return;
    }

    fetchMetrics();
  }, [properties, selectedProperty, fetchMetrics]);

  // Fetch occupancy alerts when property changes
  useEffect(() => {
    if (selectedProperty) {
      fetchOccupancyAlerts();
    }
  }, [selectedProperty, fetchOccupancyAlerts]);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);

    // Set default dates when Custom is selected
    if (newPeriod === 'custom' && !customStartDate && !customEndDate) {
      const today = new Date();
      const yearStart = new Date(today.getFullYear(), 0, 1);

      setCustomStartDate(yearStart.toISOString().split('T')[0]);
      setCustomEndDate(today.toISOString().split('T')[0]);
    }
  };

  const handleDateChange = (type, value) => {
    if (type === 'start') {
      setCustomStartDate(value);
      // If end date is before new start date, clear it
      if (customEndDate && new Date(value) > new Date(customEndDate)) {
        setCustomEndDate('');
      }
    } else {
      setCustomEndDate(value);
      // Validate end date is not before start date
      if (customStartDate && new Date(value) < new Date(customStartDate)) {
        setError('تاريخ النهاية يجب أن يكون بعد تاريخ البداية');
        return;
      }
    }
  };

  /* Excel export REMOVED (Adil, 2026-07-30). `exportTransactions` + `buildWorkbook`
     lived here and were the only consumers of the `exceljs` dependency in web/src.
     This removed a capability every role had, owners included — recoverable from git
     if anyone was relying on it. Supersedes the "existing Excel export stays as-is"
     line in tasks/owner-monthly-report.md §2. */

  if (loading && !metrics) {
    return (
      <DesktopLayout
        activeNavItem={activeNavItem}
        setActiveNavItem={setActiveNavItem}
        headerTitle="التقارير و الادارة المالية"
      >
        <div className="revenue-optimization-dashboard" dir="rtl">
          <div className="loading-container">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="التقارير و الادارة المالية"
    >
      <div className="revenue-optimization-dashboard" dir="rtl">
        {/* Account Switcher - For admin/ops only */}
        {(user?.role === 'admin' || user?.role === 'ops') && (
          <div style={{ marginBottom: '1.5rem' }}>
            <AccountSwitcher />
          </div>
        )}

        {/* Header */}
        <div className="dashboard-header">
          <div className="header-content">
            <h1>
              <i className="bi bi-graph-up-arrow"></i>
              التقارير و الادارة المالية
            </h1>
            <p className="header-subtitle">
              تحليل شامل للأداء والإيرادات مع رؤى قابلة للتنفيذ
            </p>
            {lastUpdated && (
              <p className="last-updated">
                <i className="bi bi-clock-history"></i>
                آخر تحديث: {formatRelativeTime(lastUpdated)}
              </p>
            )}
          </div>
          <div className="header-actions">
            {/* Testing Only: Generate Alerts Button - Admin/Ops, dev builds only
                (occupancy alerts are cron-generated in prod; this manual trigger
                is a test harness for that engine) */}
            {process.env.NODE_ENV !== 'production' &&
             (user?.role === 'admin' || user?.role === 'ops-manager') && selectedProperty && (
              <button
                className="btn-action"
                onClick={handleGenerateAlerts}
                disabled={generatingAlerts}
                style={{ background: '#FF9800' }}
              >
                <i className={`bi ${generatingAlerts ? 'bi-hourglass-split' : 'bi-bell'}`}></i>
                {generatingAlerts ? 'جاري التوليد...' : 'توليد تنبيهات (اختبار)'}
              </button>
            )}

            {/* Refresh Button - Admin/Ops Only */}
            {(user?.role === 'admin' || user?.role === 'ops-manager') && (
              <button className="btn-action" onClick={fetchMetrics}>
                <i className="bi bi-arrow-clockwise"></i>
                تحديث
              </button>
            )}

            {/* Back Button - Admin/Ops Only */}
            {(user?.role === 'admin' || user?.role === 'ops-manager') && (
              <Link to="/dashboard" className="btn-back">
                <i className="bi bi-arrow-right"></i>
                العودة
              </Link>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
            <button type="button" className="btn btn-outline-danger btn-sm touch-btn"
              style={{ marginInlineStart: '12px' }}
              onClick={() => (properties.length ? fetchMetrics() : fetchProperties())}>
              {t('states.retry')}
            </button>
          </div>
        )}
        {noUnits && !error && (
          <div className="alert alert-info" role="status">
            <i className="bi bi-info-circle" style={{ marginInlineEnd: '6px' }}></i>
            {t('financePage.noUnits')}
          </div>
        )}

        {/* Property Switcher - Show if any properties exist */}
        {properties.length > 0 && (
          <PropertySwitcher
            properties={properties}
          />
        )}

        {/* Date Range Selector */}
        <DateRangeSelector
          period={period}
          onPeriodChange={handlePeriodChange}
          startDate={customStartDate}
          endDate={customEndDate}
          onDateChange={handleDateChange}
        />

        {/* No Data Message */}
        {!loading && metrics && metrics.bookings?.total === 0 && (
          <div className="no-bookings-message">
            <div className="alert alert-info">
              <i className="bi bi-info-circle"></i>
              <span>لا توجد حجوزات في الفترة المحددة. جرّب فترة زمنية مختلفة.</span>
            </div>
          </div>
        )}

        {/* Performance Metrics Cards */}
        <div className="metrics-section">
          <PerformanceMetrics
            metrics={metrics}
            comparison={comparison}
            loading={loading}
            /* An in-progress period is measured to-date; for the current month, ops
               reports the same month in full — so the two pages differ by design
               until it closes. */
            toDate={periodInProgress}
            crossRefMonth={periodInProgress && period === 'thisMonth'}
          />
        </div>

        {/* Platform Breakdown */}
        {metrics && metrics.bookings?.total > 0 && (
          <div className="platform-section">
            <div className="section-card">
              <div
                className="section-header"
                style={{ cursor: 'pointer' }}
                onClick={() => setPlatformExpanded(!platformExpanded)}
              >
                <h3>
                  <i className={`bi ${platformExpanded ? 'bi-chevron-down' : 'bi-chevron-left'}`}></i>
                  <i className="bi bi-diagram-3"></i>
                  أداء المنصات - توزيع الحجوزات حسب المنصة
                  {!platformExpanded && (
                    <span className="badge bg-primary ms-2">
                      {metrics?.byPlatform?.length || 0} منصة
                    </span>
                  )}
                </h3>
              </div>
              {platformExpanded && (
                <PlatformBreakdown
                  platforms={metrics?.byPlatform || []}
                  loading={loading}
                  hideHeader={true}
                />
              )}
            </div>
          </div>
        )}

        {/* Day of Week Analysis */}
        {metrics?.byDayOfWeek && metrics.byDayOfWeek.length > 0 && (
          <div className="day-of-week-section">
            <div className="section-card">
              <div
                className="section-header"
                style={{ cursor: 'pointer' }}
                onClick={() => setDayOfWeekExpanded(!dayOfWeekExpanded)}
              >
                <h3>
                  <i className={`bi ${dayOfWeekExpanded ? 'bi-chevron-down' : 'bi-chevron-left'}`}></i>
                  <i className="bi bi-calendar-week"></i>
                  تحليل الحجوزات حسب أيام الأسبوع
                  {!dayOfWeekExpanded && (
                    <span className="badge bg-primary ms-2">
                      {metrics.byDayOfWeek.length} أيام
                    </span>
                  )}
                </h3>
                {dayOfWeekExpanded && (
                  <p className="section-subtitle">تحديد الأنماط وفرص التسعير</p>
                )}
              </div>
              {dayOfWeekExpanded && (
                <div className="day-of-week-grid">
                {metrics.byDayOfWeek.map((day, index) => (
                  <div key={index} className="day-card">
                    <div className="day-name">{formatWeekdayName(day.dayOfWeek)}</div>
                    <div className="day-stats">
                      <div className="stat-item">
                        <small>حجوزات تبدأ في هذا اليوم</small>
                        <strong>{day.bookings}</strong>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Insights Panel - Value-Driven Actionable Insights (hidden for TurnKey owners) */}
        {metrics && !isTurnKeyOwner && (
          <div className="insights-panel">
            <div className="section-card">
              <div
                className="section-header"
                style={{ cursor: 'pointer' }}
                onClick={() => setInsightsExpanded(!insightsExpanded)}
              >
                <h3>
                  <i className={`bi ${insightsExpanded ? 'bi-chevron-down' : 'bi-chevron-left'}`}></i>
                  <i className="bi bi-lightbulb"></i>
                  تحليل آداء الحجوزات للفترة السابقة
                  {!insightsExpanded && (
                    <span className="badge bg-primary ms-2">
                      {(() => {
                        let count = 0;
                        if (metrics.byPlatform?.find(p => p.platform === 'direct')?.grossRevenue > 0) count++;
                        if (comparison?.revenue && comparison.revenue.direction !== 'neutral') count++;
                        if (metrics.bookings?.averageStayLength > 0 && metrics.bookings.averageStayLength < 2) count++;
                        if (metrics.occupancy?.rate < 50) count++;
                        return count > 0 ? `${count} توصية` : 'عرض';
                      })()}
                    </span>
                  )}
                </h3>
                <p className="section-subtitle">{getDateRangeText()}</p>
              </div>
              {insightsExpanded && (
                <div className="insights-grid">
                {/* 1. Direct Booking Savings Insight
                    The saving is a COUNTERFACTUAL ("what an OTA would have taken"), so the rate
                    must be one this portfolio actually paid — it used to be a hardcoded 15%.
                    The engine already returns real per-platform commission in this same response,
                    so the blended OTA rate is derived from it: total OTA commission ÷ total OTA
                    gross for the selected period. With no OTA bookings in the period there is no
                    observed rate, so the card states the benefit WITHOUT inventing a number.
                    Audit finding F-M2, tasks/audit-occupancy-revenue-calculator-inventory.md. */}
                {(() => {
                  const directBooking = metrics.byPlatform?.find(p => p.platform === 'direct');
                  if (!directBooking || !(directBooking.grossRevenue > 0)) return null;

                  // Only platforms that ACTUALLY charged a fee set the counterfactual rate.
                  // 'other' (manual/offline entries) and Gathern (fee is guest-borne, so ours
                  // is 0) carry no commission — including them would dilute the rate toward
                  // zero and understate a real saving.
                  const otaPlatforms = (metrics.byPlatform || [])
                    .filter(p => p.platform !== 'direct' && p.commission > 0 && p.grossRevenue > 0);
                  const otaGross = otaPlatforms.reduce((sum, p) => sum + (p.grossRevenue || 0), 0);
                  const otaCommission = otaPlatforms.reduce((sum, p) => sum + (p.commission || 0), 0);
                  const blendedRate = otaGross > 0 ? otaCommission / otaGross : null;
                  const savedCommission = blendedRate === null ? null : directBooking.grossRevenue * blendedRate;

                  return (
                    <div className="insight-card success-money">
                      <i className="bi bi-piggy-bank"></i>
                      <div className="insight-content">
                        <strong>
                          {savedCommission === null
                            ? 'الحجز المباشر يوفّر عمولة المنصات! 🎉'
                            : `وفّرت ${savedCommission.toFixed(0)} ر.س من العمولات! 🎉`}
                        </strong>
                        <ul className="insight-recommendations">
                          <li>
                            {blendedRate === null
                              ? 'الحجز المباشر = 0% عمولة'
                              : `الحجز المباشر = 0% عمولة (بدلاً من ${(blendedRate * 100).toFixed(1)}% — متوسط ما دفعته للمنصات في هذه الفترة)`}
                          </li>
                          <li>شارك رابط الحجز المباشر مع الضيوف السابقين لزيادة الحجوزات المباشرة</li>
                        </ul>
                      </div>
                    </div>
                  );
                })()}

                {/* 2. Revenue Comparison — unchanged revenue is not a decline, skip it */}
                {comparison?.revenue && comparison.revenue.direction !== 'neutral' && (
                  <div className={`insight-card ${comparison.revenue.direction === 'up' ? 'success' : 'warning'}`}>
                    <i className={`bi ${comparison.revenue.direction === 'up' ? 'bi-graph-up' : 'bi-graph-down'}`}></i>
                    <div className="insight-content">
                      <strong>
                        {comparison.revenue.direction === 'up'
                          ? `نمو الإيرادات: +${Math.abs(comparison.revenue.change).toFixed(1)}%`
                          : `انخفاض الإيرادات: ${comparison.revenue.change.toFixed(1)}%`}
                      </strong>
                      <ul className="insight-recommendations">
                        {comparison.revenue.direction === 'up' ? (
                          <>
                            <li>أداء ممتاز! استمر بنفس الاستراتيجية</li>
                            <li>شارك العقار أكثر على منصات التواصل الاجتماعي</li>
                          </>
                        ) : (
                          <>
                            <li>راجع الأسعار ومقارنتها بالمنافسين في نفس المنطقة</li>
                            <li>زد من التسويق والترويج لاستعادة الإيرادات</li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                )}

                {/* 3. Short Stay Warning */}
                {metrics.bookings?.averageStayLength > 0 && metrics.bookings.averageStayLength < 2 && (
                  <div className="insight-card warning-action">
                    <i className="bi bi-clock-history"></i>
                    <div className="insight-content">
                      <strong>إقامات قصيرة جداً: {metrics.bookings.averageStayLength.toFixed(1)} ليلة فقط</strong>
                      <ul className="insight-recommendations">
                        <li><strong>المشكلة:</strong> تكاليف تنظيف وتشغيل عالية مقارنة بالإيراد</li>
                        <li><strong>التوصية:</strong> تطبيق خصم 10% للحجوزات 3 ليالي+ لزيادة الربحية</li>
                        <li><strong>بديل:</strong> عرض أسبوعي خاص (7 ليالي بسعر 6 ليالي)</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* 4. Occupancy Warning */}
                {metrics.occupancy?.rate < 50 && (
                  <div className="insight-card warning-action">
                    <i className="bi bi-exclamation-triangle"></i>
                    <div className="insight-content">
                      <strong>معدل إشغال منخفض: {metrics.occupancy.rate.toFixed(1)}%</strong>
                      <ul className="insight-recommendations">
                        {/* The SAR figure is empty nights valued at the period's REAL ADR. It used
                            to fall back to a hardcoded 320 ر.س/ليلة, which fired exactly when ADR
                            was unknowable (zero bookings ⇒ no rate to observe) — inventing revenue
                            precisely when there was none. Now the nights show alone in that case.
                            Audit finding F-M2. */}
                        <li>
                          {(metrics.occupancy.totalNights - metrics.occupancy.bookedNights)} ليالي فارغة
                          {metrics.rates?.adr > 0 &&
                            ` = ${((metrics.occupancy.totalNights - metrics.occupancy.bookedNights) * metrics.rates.adr).toFixed(0)} ر.س إيراد محتمل ضائع (بمتوسط سعرك الحالي)`}
                        </li>
                        <li><strong>التوصية:</strong> خفض السعر 10% لزيادة الحجوزات</li>
                        <li><strong>بديل:</strong> عرض خاص للحجز المبكر أو عروض اللحظة الأخيرة</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* No Insights Available — neutral (unchanged) revenue counts as no insight */}
                {!metrics.byPlatform?.find(p => p.platform === 'direct') &&
                 (!comparison?.revenue || comparison.revenue.direction === 'neutral') &&
                 metrics.occupancy?.rate >= 50 &&
                 !(metrics.bookings?.averageStayLength > 0 && metrics.bookings.averageStayLength < 2) && (
                  <div className="insight-card info">
                    <i className="bi bi-info-circle"></i>
                    <div className="insight-content">
                      <strong>أداء جيد! 👍</strong>
                      <p>عقارك يعمل بشكل جيد. استمر في تقديم خدمة ممتازة للضيوف.</p>
                    </div>
                  </div>
                )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Intelligent Revenue Alerts - Occupancy Optimization (hidden for TurnKey owners) */}
        {!loading && metrics && selectedProperty && !isTurnKeyOwner && (
          <div className="revenue-alerts-panel">
            <div className="section-card">
              <div
                className="section-header"
                style={{ cursor: 'pointer' }}
                onClick={() => setAlertsExpanded(!alertsExpanded)}
              >
                <h3>
                  <i className={`bi ${alertsExpanded ? 'bi-chevron-down' : 'bi-chevron-left'}`}></i>
                  <i className="bi bi-bell"></i>
                  تنبيهات ذكية لتحسين الإيرادات للفترة القادمة
                  {!alertsExpanded && occupancyAlerts.length > 0 && (
                    <span className="badge bg-danger ms-2">
                      {occupancyAlerts.length} تنبيه
                    </span>
                  )}
                  {!alertsExpanded && occupancyAlerts.length === 0 && (
                    <span className="badge bg-success ms-2">
                      لا توجد تنبيهات
                    </span>
                  )}
                </h3>
                {alertsExpanded && (
                  <p className="section-subtitle">
                    تنبيهات تلقائية بناءً على تحليل نسبة الإشغال والأداء
                  </p>
                )}
              </div>

              {alertsExpanded && (
                <div className="alerts-container">
                {alertsLoading ? (
                  <div className="alert-loading">
                    <i className="bi bi-hourglass-split"></i>
                    <span>جاري تحميل التنبيهات...</span>
                  </div>
                ) : occupancyAlerts.length > 0 ? (
                  occupancyAlerts.map((alert) => (
                    <div key={alert._id} className={`revenue-alert ${alert.severity}`}>
                      <div className="alert-icon">
                        {alert.severity === 'low' && <i className="bi bi-info-circle"></i>}
                        {alert.severity === 'medium' && <i className="bi bi-exclamation-triangle"></i>}
                        {alert.severity === 'high' && <i className="bi bi-exclamation-octagon"></i>}
                      </div>
                      <div className="alert-content">
                        <div className="alert-header">
                          <strong>{alert.title}</strong>
                          <span className={`alert-status ${alert.status}`}>
                            {alert.status === 'new' && 'جديد'}
                            {alert.status === 'viewed' && 'تم المشاهدة'}
                            {alert.status === 'acknowledged' && 'تم الاطلاع'}
                          </span>
                        </div>
                        <p className="alert-message">{alert.message}</p>

                        {/* Event Information - Phase 2 Integration */}
                        {alert.eventInfo && alert.eventInfo.hasEvents && alert.eventInfo.events.length > 0 && (
                          <div className="alert-events">
                            <div className="events-header">
                              <i className="bi bi-calendar-event text-success"></i>
                              <strong>فرصة: {alert.eventInfo.events.length} فعالية قادمة في المنطقة</strong>
                            </div>
                            <div className="events-list">
                              {alert.eventInfo.events.slice(0, 2).map((event, idx) => (
                                <div key={idx} className="event-item">
                                  <i className="bi bi-dot"></i>
                                  <span>{event.nameAr || event.name}</span>
                                  <span className="event-dates">
                                    ({formatDate(event.startDate, { year: undefined, month: 'short' })} -
                                    {' '}{formatDate(event.endDate, { year: undefined, month: 'short' })})
                                  </span>
                                </div>
                              ))}
                            </div>
                            <button
                              className="btn-view-events"
                              onClick={() => navigate('/events')}
                            >
                              <span>عرض الفعاليات</span>
                              <i className="bi bi-arrow-left"></i>
                            </button>
                          </div>
                        )}

                        <div className="alert-details">
                          <span className="alert-period">
                            <i className="bi bi-calendar3"></i>
                            {alert.periodDays} يوم
                          </span>
                          <span className="alert-occupancy">
                            <i className="bi bi-graph-down"></i>
                            نسبة الإشغال: {alert.occupancyRate.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-alerts">
                    <i className="bi bi-check-circle"></i>
                    <p>لا توجد تنبيهات حالياً</p>
                    <span>نسبة الإشغال جيدة! 👍</span>
                  </div>
                )}

                {/* Consolidated Recommendations Section */}
                {occupancyAlerts.length > 0 && (() => {
                  // Extract all unique recommendations from all alerts
                  const allRecommendations = [];
                  const seenTexts = new Set();

                  occupancyAlerts.forEach(alert => {
                    if (alert.recommendations && alert.recommendations.length > 0) {
                      alert.recommendations.forEach(rec => {
                        const text = rec.text || rec;
                        if (!seenTexts.has(text)) {
                          seenTexts.add(text);
                          allRecommendations.push({
                            text,
                            priority: rec.priority || 999
                          });
                        }
                      });
                    }
                  });

                  // Sort by priority
                  allRecommendations.sort((a, b) => a.priority - b.priority);

                  if (allRecommendations.length === 0) return null;

                  return (
                    <div className="consolidated-recommendations">
                      <div className="recommendations-header">
                        <i className="bi bi-lightbulb"></i>
                        <strong>التوصيات لتحسين الإشغال والإيرادات</strong>
                      </div>
                      <ul className="recommendations-list">
                        {allRecommendations.map((rec, idx) => (
                          <li key={idx}>{rec.text}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Monthly owner reports — reads the frozen snapshot, independent of this
            page's date range. Renders for both roles; the section hides itself for
            an owner with no released report yet. */}
        <MonthlyReportsSection />

        {/* Footer Note */}
        <div className="dashboard-footer">
          <p>
            <i className="bi bi-info-circle"></i>
            البيانات محسوبة من الحجوزات المكتملة والنشطة فقط. الحجوزات الملغاة غير مشمولة.
          </p>
        </div>
      </div>
    </DesktopLayout>
  );
};

export default RevenueOptimizationDashboard;
